import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  styled,
  Fab,
  Stack,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/footer';
import { apigetPublic } from '../../service/api';
import DashboardFilterBar, { DashboardNoMatches } from '../../components/dashboard/DashboardFilterBar';
import useDashboardFilters from '../../components/dashboard/useDashboardFilters';

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
const countOf = (field) => (s) => s[field]?.length || 0;
const mostFirst = (field) => (a, b) => countOf(field)(b) - countOf(field)(a) || byName(a, b);

const FILTER_CONFIG = {
  search: (s) => [s.name, s.code, ...(s.additionalOptions || [])],
  filters: [
    {
      key: 'lo',
      label: 'Loan officer',
      values: (s) => (s.loanOfficers || []).map((lo) => `${lo.firstName} ${lo.lastName}`),
    },
    {
      key: 'branches',
      label: 'Has branches',
      keepOrder: true,
      values: (s) => [s.branches?.length ? 'Yes' : 'No'],
    },
  ],
  sorts: [
    { key: 'name', label: 'Name A–Z', compare: byName },
    { key: 'los', label: 'Most loan officers', compare: mostFirst('loanOfficers') },
    { key: 'branches', label: 'Most branches', compare: mostFirst('branches') },
  ],
  defaultSort: 'name',
};

const DashboardContainer = styled(Container)(() => ({
  paddingTop: '8px',
  paddingBottom: '50px',
}));

const StateCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: theme.shadows[3],
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.shadows[6],
  },
}));

const StateLink = styled(Link)(() => ({
  textDecoration: 'none',
  color: 'inherit',
}));

const StateImage = styled(CardMedia)(() => ({
  height: 180,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
}));

export default function StateDashboard() {
  const [states, setStates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStates = async () => {
      const result = await apigetPublic('states/search');
      if (result?.status === 200 && Array.isArray(result.data?.result)) {
        setStates(result.data.result);
      }
      setIsLoading(false);
    };
    fetchStates();
  }, []);

  const { visible, total, active, clear, barProps } = useDashboardFilters(states, FILTER_CONFIG);

  const normalizeUrl = (url) => {
    if (!url) return "/assets/images/no-image.jpg";
    return url.startsWith("http")
      ? url
      : `https://${url.replace(/^https?:\/\//, "")}`;
  };

  return (
    <>

      <Box
        sx={{
          backgroundImage: 'url(/assets/backgroundimageforherobanner.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(255,255,255,0.9)',
            minHeight: '100vh',
            position: 'relative',
          }}
        >
          <DashboardContainer>
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
              State Dashboard
            </Typography>

            <DashboardFilterBar
              noun="states"
              {...barProps}
              shown={visible.length}
              total={total}
              active={active}
              onClear={clear}
            />

            {isLoading ? (
              <Typography align="center">Loading states...</Typography>
            ) : states.length > 0 && visible.length === 0 ? (
              <DashboardNoMatches noun="states" onClear={clear} />
            ) : states.length > 0 ? (
              <Grid container spacing={4}>
                {visible.map((state) => (
                  <Grid item xs={12} sm={6} md={4} key={state._id}>
                    <StateLink to={`/dashboard/states/${state._id}`}>
                      <StateCard>

                        {/* State Image */}
                        <StateImage image={normalizeUrl(state.branchImageUrl)} />

                        <CardContent>
                          <Typography variant="h5" sx={{ mb: 1 }}>
                            {state.name} ({state.code})
                          </Typography>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Options: {state.additionalOptions?.length || 0}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Branches: {state.branches?.length || 0}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Loan Officers: {state.loanOfficers?.length || 0}
                          </Typography>
                        </CardContent>

                        
                      </StateCard>
                    </StateLink>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography align="center" sx={{ mt: 4 }}>
                No states found.
              </Typography>
            )}
          </DashboardContainer>

          <Fab
            color="primary"
            aria-label="add"
            onClick={() => navigate('/dashboard/states/create')}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#115293' },
            }}
          >
            <AddIcon />
          </Fab>
        </Box>
      </Box>

      <Box sx={(theme) => ({ ...theme.mixins.toolbar })} />
      <Footer />
    </>
  );
}
